import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, tap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { ApiUrlService } from './api-url.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly tokenKey = 'coffeeclub_token';
  private readonly userKey = 'coffeeclub_user';

  readonly accessToken = signal<string | null>(localStorage.getItem(this.tokenKey));
  readonly user = signal<AuthUser | null>(this.loadUser());
  readonly user$ = toObservable(this.user);

  constructor(private readonly http: HttpClient, private readonly apiUrl: ApiUrlService) {}

  login(email: string, password: string): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(this.apiUrl.build('/api/auth/login'), { email, password }).pipe(
      tap(response => {
        this.accessToken.set(response.accessToken);
        this.user.set(response.user);
        localStorage.setItem(this.tokenKey, response.accessToken);
        localStorage.setItem(this.userKey, JSON.stringify(response.user));
      })
    );
  }

  register(request: RegisterRequest): Observable<AuthUser> {
    return this.http.post<AuthUser>(this.apiUrl.build('/api/auth/register'), request);
  }

  listPublicPlateaux(): Observable<PublicPlateau[]> {
    return this.http.get<PublicPlateau[]>(this.apiUrl.build('/api/auth/plateaux'));
  }

  requestPasswordReset(email: string): Observable<void> {
    return this.http.post<void>(this.apiUrl.build('/api/auth/reset-password/request'), { email });
  }

  confirmPasswordReset(token: string, password: string): Observable<void> {
    return this.http.post<void>(this.apiUrl.build('/api/auth/reset-password/confirm'), { token, password });
  }

  logout(): void {
    this.accessToken.set(null);
    this.user.set(null);
    localStorage.removeItem(this.tokenKey);
    localStorage.removeItem(this.userKey);
  }

  isAuthenticated(): boolean {
    return !!this.accessToken();
  }

  isAdmin(): boolean {
    return this.user()?.role === 'ADMIN';
  }

  private loadUser(): AuthUser | null {
    const raw = localStorage.getItem(this.userKey);
    if (!raw) {
      return null;
    }
    try {
      return JSON.parse(raw) as AuthUser;
    } catch {
      return null;
    }
  }
}

export interface AuthUser {
  id: number;
  prenom: string;
  nom: string;
  email: string;
  role: 'ADMIN' | 'USER';
  plateau: {
    id: number;
    code: string;
  };
}

export interface AuthResponse {
  accessToken: string;
  user: AuthUser;
}

export interface RegisterRequest {
  email: string;
  prenom: string;
  nom: string;
  password: string;
  plateauId: number;
}

export interface PublicPlateau {
  id: number;
  name: string;
}
